export class BitrateManager {
  constructor(adapter, resolutionMapping) {
    this._adapter = adapter;
    this._bitrates = adapter.bitratesObject();

    this.resolutionMapping = resolutionMapping;
    this.representations = this.getRepresentations();
  }

  get adapter() {
    return this._adapter;
  }

  rawRepresentations() {
    return this.bitrates.representations();
  }

  dashObject() {
    return this.adapter.dashObject();
  }

  hlsObject() {
    return this.adapter.hlsObject();
  }

  get bitrates() {
    return this._bitrates;
  }

  getResolutions() {
    const autoResolution = {
      label: "Auto",
      mapping: null,
      representation: this.representations.find((rep) => rep.height === "auto"),
      enableQuality: () => {
        this.triggerEvent("auto");
        this.enableQuality("auto", "Auto");
      },
    };

    let resolutions = [];

    if (!this.resolutionMapping || this.resolutionMapping.length === 0) {
      resolutions = this.representations
        .filter((rep) => rep.height !== "auto")
        .map((rep) => {
          const quality = this.getRepresentationResolution(rep)
          const label = `${quality}p`;

          return {
            label,
            mapping: null,
            representation: rep,
            enableQuality: () => {
              this.triggerEvent(quality);
              this.enableQuality(quality, label);
            },
          };
        });
    } else {
      resolutions = this.resolutionMapping
        .map((map) => {
          const rep = this.availableRepresentation(map);
          const quality = this.getRepresentationResolution(rep)
          return {
            label: map.name,
            mapping: map,
            representation: rep,
            enableQuality: () => {
              this.triggerEvent(quality);
              this.enableQualityWithAbr(quality, map.name);
            },
          };
        })
        .filter((it) => !!it.representation);
    }

    return [autoResolution, ...resolutions];
  }

  static autoRepresentation() {
    return { height: "auto", enabled: function () {} };
  }

  getRepresentations() {
    const rep = [];

    let rawRepresentations = this.bitrates.representations();

    const sortedRepresentation = {};
    for (const element of rawRepresentations) {
      sortedRepresentation[element.height] = element;
    }

    rawRepresentations = Object.values(sortedRepresentation);

    for (const element of rawRepresentations) {
      if (typeof element.height != "undefined" && element.height !== 0) {
        let exists = false;
        for (let j in rep) {
          if (rep[j].height === element.height) {
            exists = true;
          }
        }

        if (!exists) {
          rep.push(element);
        }
      }
    }
    rep.push(BitrateManager.autoRepresentation());
    rep.reverse();

    return rep;
  }

  getRepresentationResolution(representation) {
    if (representation.width < representation.height)
      return representation.width;
    return representation.height;
  }

  availableRepresentation(mapping) {
    const matchingRepresentations = this.representations.filter((rep) => {
      const resolution = this.getRepresentationResolution(rep);
      return mapping.min <= resolution && mapping.max >= resolution;
    });

    if (matchingRepresentations.length > 1) {
      return matchingRepresentations.reduce((acc, val) => {
        if (acc === null) return val;

        const resolution = this.getRepresentationResolution(val);
        const accResolution = this.getRepresentationResolution(acc);

        return accResolution < resolution ? val : acc;
      }, null);
    }
    return matchingRepresentations[0];
  }

  getActiveQuality() {
    try {
      return window["playback_selected_quality"][this.adapter.id()] || "Auto"
    } catch (err) {
      return "Auto"
    }
  }

  enableQuality(quality, label) {
    if (this.adapter.hasDashObject()) {
      this.enableDashQuality(quality);
    } else {
      this.enableHlsQuality(quality);
    }

    const playerId = this.adapter.id();
    if (window["playback_selected_quality"]) {
      window["playback_selected_quality"][playerId] = label;
    } else {
      window["playback_selected_quality"] = {
        [playerId]: label,
      };
    }
  }

  enableQualityWithAbr(quality, label) {
    if (this.adapter.hasDashObject()) {
      this.enableDashQualityWithAbr(quality);
    } else {
      this.enableHlsQualityWithAbr(quality);
    }

    const playerId = this.adapter.id();
    if (window["playback_selected_quality"]) {
      window["playback_selected_quality"][playerId] = label;
    } else {
      window["playback_selected_quality"] = {
        [playerId]: label,
      };
    }
  }

  enableDashQuality(quality) {
    if (quality === "auto") {
      this.dashObject().enableAutoSwitchQuality();
      return;
    }

    this.rawRepresentations().forEach((representation) => {
      if (this.getRepresentationResolution(representation) === quality) {
        representation.enable();
      }
    });
  }

  enableHlsQuality(quality) {
    this.rawRepresentations().forEach((representation) => {
      if (this.getRepresentationResolution(representation) === quality) {
        representation.enabled(true);
      } else if (quality === "auto") {
        representation.enabled(true);
      } else {
        representation.enabled(false);
      }
    });
  }

  enableHlsQualityWithAbr(quality) {
    this.rawRepresentations().forEach((representation) => {
      if (quality === "auto") {
        representation.enabled(true);
      } else if (this.getRepresentationResolution(representation) > quality) {
        representation.enabled(false);
      } else {
        representation.enabled(true);
      }
    });
  }

  enableDashQualityWithAbr(quality) {
    if (quality === "auto") {
      this.dashObject().enableAutoSwitchQuality();
      return;
    }

    this.rawRepresentations().forEach((representation) => {
      if (this.getRepresentationResolution(representation) == quality) {
        representation.enableAsMaxQuality();
      }
    });
  }

  triggerEvent(quality) {
    this.adapter.trigger({ type: "vp-bitrate-change", height: quality });
  }
}
